import { Trans } from "@lingui/react/macro";
import { CheckCircleFilled, CloseCircleFilled, DownloadOutlined } from "@ant-design/icons";
import React, { Suspense } from "react";
import { useLingui } from "@lingui/react";
import { Await, Link, useRouteError } from "react-router";
import { Button } from "antd";

import { FallbackScreen } from "../../../lib/ui-components/fallback-screen";
import { LoadingScreen } from "../../../lib/ui-components/loading";
import measuringDeviceMissing from "./images/no-connection-warning.svg?url";
import addMeasurementPointExampleDe from "./images/add-measurement-point-example-de.svg?url";
import addMeasurementPointExampleEn from "./images/add-measurement-point-example-en.svg?url";
import startDataAcquisitionsExampleDe from "./images/start-data-acquisitions-example-de.svg?url";
import startDataAcquisitionsExampleEn from "./images/start-data-acquisitions-example-en.svg?url";
import agentGraphic from "./images/advertise-agent.svg?url";
import agentOfflineSvg from "./images/no-connection-error.svg?url";
import agentOfflineManual from "./agent-offline-manual.pdf";
import { MonitoringUnavailableError } from "./monitoring-error";
import { AppLayout } from "../../../lib/ui-components/layout/app-layout";
import { NoActiveEnergyImportDataError, NoElectricityMainMeterError } from "../lib/prosumers";
import { makeSymbol } from "../domain";
import missingDataAcquisitionsDe from "./images/missing-data-acquisitions-de.svg?url";
import missingDataAcquisitionsEn from "./images/missing-data-acquisitions-en.svg?url";
import missingActiveEnergyImportDe from "./images/missing-active-energy-import-de.svg?url";
import missingActiveEnergyImportEn from "./images/missing-active-energy-import-en.svg?url";

export const fallbackActionButtons = [
  <Button
    key={0}
    href={"mailto:service@enit.io?subject=Support-Anfrage Enit hub"}
    target="_blank"
    size="large"
  >
    <Trans>Contact support</Trans>
  </Button>,
  <Link key={1} to="/app/plan">
    <Button type="primary" size="large">
      <Trans>To your measuring concept</Trans>
    </Button>
  </Link>,
];

export function AgentNotAvailable() {
  return (
    <FallbackScreen
      headingText={
        <Trans>Discover all capabilities of our energy monitoring software Enit agent!</Trans>
      }
      description={
        <Trans>
          Get access to exclusive features around energy monitoring with the paid Enit agent. For a
          first impression, check out our free demo, visit our{" "}
          <a
            className="text-magenta-500"
            href="https://enit.io/"
            target="_blank"
            rel="noopener noreferrer"
          >
            website
          </a>{" "}
          or contact us directly for a personal consultation.
          <br />
          Our team is looking forward to accompanying you on the way to more energy efficiency and
          sustainability.
        </Trans>
      }
      actionButtons={[
        <Button
          key={0}
          href="https://enit.io/kontakt?utm_campaign=EN_Besucher-hub_xde&utm_source=hub"
          type="primary"
          target="_blank"
          size="large"
        >
          <Trans>Contact us</Trans>
        </Button>,
        <Button key={1} href="http://demo.enit-systems.com/" target="_blank" size="large">
          <Trans>Free Demo</Trans>
        </Button>,
      ]}
      svgImage={<img alt="Demonstration of a line chart" src={agentGraphic} />}
    />
  );
}

export function AgentOffline() {
  return (
    <FallbackScreen
      headingText={<Trans>No connection to the Enit agent</Trans>}
      description={
        <Trans>
          Could not establish connection to Enit agent. Download the troubleshooting manual to fix
          the problem or try again later.
        </Trans>
      }
      actionButtons={[
        <Button
          key={0}
          href={agentOfflineManual}
          download="Sichtkontrolle ENIT agent in Baugruppe.pdf"
          size="large"
          icon={<DownloadOutlined />}
        >
          <Trans>Download manual</Trans>
        </Button>,
        <Button key={1} type="primary" size="large" onClick={() => window.location.reload()}>
          <Trans>Try again</Trans>
        </Button>,
      ]}
      svgImage={<img src={agentOfflineSvg} alt="" aria-hidden />}
    />
  );
}

function MissingSteps({
  hasMeasuringDevices,
  hasProsumersInEnergySystem,
}: {
  hasMeasuringDevices: boolean;
  hasProsumersInEnergySystem: boolean;
}) {
  const { i18n } = useLingui();

  const svgImage = !hasMeasuringDevices ? (
    <img src={measuringDeviceMissing} alt="" aria-hidden />
  ) : !hasProsumersInEnergySystem ? (
    <img
      src={i18n.locale === "de" ? addMeasurementPointExampleDe : addMeasurementPointExampleEn}
      alt=""
      aria-hidden
    />
  ) : (
    <img
      alt="Demonstration of measurements concept"
      src={i18n.locale === "de" ? startDataAcquisitionsExampleDe : startDataAcquisitionsExampleEn}
    />
  );
  return (
    <FallbackScreen
      headingText={<Trans>This is how your energy data gets into the Enit hub:</Trans>}
      description={
        <ul className="space-y-1">
          <li className="flex gap-2">
            <StatusIcon ok={hasMeasuringDevices} />
            <span>
              <Trans>Configure a meter in your Enit agent</Trans>
            </span>
          </li>
          <li className="flex gap-2">
            <StatusIcon ok={hasProsumersInEnergySystem} />
            <span>
              <Trans>Create a measuring concept with measurement points</Trans>
            </span>
          </li>
          <li className="flex gap-2">
            <StatusIcon ok={false} />
            <span>
              <Trans>Start data acquisitions for your measurement points</Trans>
            </span>
          </li>
        </ul>
      }
      actionButtons={fallbackActionButtons}
      svgImage={svgImage}
    />
  );
}

export function StatusIcon({ ok }: { ok: boolean }) {
  return ok ? (
    <CheckCircleFilled className="text-grass-green-500" />
  ) : (
    <CloseCircleFilled className="text-red-500" />
  );
}

export function MonitoringFallback() {
  const error = useRouteError();
  if (!(error instanceof MonitoringUnavailableError)) {
    throw error;
  }

  return (
    <AppLayout name={""}>
      {!error.hasAgents ? (
        <AgentNotAvailable />
      ) : (
        <Suspense fallback={<LoadingScreen wScreenDisabled={true} />}>
          <Await resolve={error.hasMeasuringDevicesPromise} errorElement={<AgentOffline />}>
            {(hasMeasuringDevices: boolean) => (
              <MissingSteps
                hasMeasuringDevices={hasMeasuringDevices}
                hasProsumersInEnergySystem={error.hasProsumersInEnergySystem}
              />
            )}
          </Await>
        </Suspense>
      )}
    </AppLayout>
  );
}

export function NoElectricityMainMeterFallback({ title }: { title: React.ReactNode }) {
  const error = useRouteError();
  if (!(error instanceof NoElectricityMainMeterError)) {
    throw error;
  }
  const { i18n } = useLingui();

  return (
    <FallbackScreen
      headingText={title}
      description={
        <span className="flex gap-2 items-start">
          <span>
            <StatusIcon ok={false} />
          </span>
          <span>
            <Trans>
              Start a data acquisition with the measurands{" "}
              <span className="font-bold">{i18n._(makeSymbol("P").label)}</span> and{" "}
              <span className="font-bold">{i18n._(makeSymbol("E", "P,IMPORT").label)}</span>
              on a measuring point that is a root node in the topology.
            </Trans>
          </span>
        </span>
      }
      actionButtons={fallbackActionButtons}
      svgImage={
        <img
          alt="Demonstation of required channels"
          src={i18n.locale === "de" ? missingDataAcquisitionsDe : missingDataAcquisitionsEn}
        />
      }
    />
  );
}

export function NoActiveEnergyImportDataFallback({ title }: { title: React.ReactNode }) {
  const error = useRouteError();
  if (!(error instanceof NoActiveEnergyImportDataError)) {
    throw error;
  }
  const { i18n } = useLingui();

  return (
    <FallbackScreen
      headingText={title}
      description={
        <span className="flex gap-2 items-start">
          <span>
            <StatusIcon ok={false} />
          </span>
          <span>
            <Trans>
              Start a data acquisition with the measurand{" "}
              <span className="font-bold">{i18n._(makeSymbol("E", "P,IMPORT").label)}</span>.
            </Trans>
          </span>
        </span>
      }
      actionButtons={fallbackActionButtons}
      svgImage={
        <img
          alt="Demonstation of required channel"
          src={i18n.locale === "de" ? missingActiveEnergyImportDe : missingActiveEnergyImportEn}
        />
      }
    />
  );
}
